// Piezas de presentación compartidas por las tres pantallas del paso 02 —KYC completo, facematch y
// comprobante de domicilio—, para que se lean como la misma familia y no como tres diseños distintos.
import type { ReactNode } from 'react';
import { Loader2, QrCode, Smartphone } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { KycModality } from '../types';

/** `.state-icon` del mock: círculo de 56 px con el tono del estado. */
export function StateIcon({
    tone,
    children,
}: {
    // `warning` (ámbar) para lo que atora sin rechazar — mismo criterio que las estaciones del KYC:
    // rojo es "esta persona no pasó", y una espera atorada no dice nada del cliente.
    tone: 'info' | 'success' | 'danger' | 'warning';
    children: ReactNode;
}) {
    return (
        <div
            className={cn(
                'mx-auto mb-3 flex size-14 items-center justify-center rounded-full',
                tone === 'info' && 'bg-info-soft text-info',
                tone === 'success' && 'bg-success-soft text-success',
                tone === 'danger' && 'bg-destructive/10 text-destructive',
                tone === 'warning' && 'bg-amber-500/10 text-amber-600',
            )}
        >
            {children}
        </div>
    );
}

/**
 * Encabezado del cuerpo: icono redondo, título de 15 px y subtítulo muted centrado.
 *
 * Es la jerarquía que comparten TODAS las pantallas del paso. Vivía copiada en cada rama del paso
 * 02, y así el comprobante terminó con otro aire que el KYC y el facematch.
 */
export function ScreenIntro({
    tone,
    icon,
    title,
    sub,
}: {
    tone: 'info' | 'success' | 'danger' | 'warning';
    icon: ReactNode;
    title: string;
    sub?: ReactNode;
}) {
    return (
        <>
            <StateIcon tone={tone}>{icon}</StateIcon>
            <p className="mb-1 text-[15px] font-semibold">{title}</p>
            {sub ? (
                <p className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                    {sub}
                </p>
            ) : null}
        </>
    );
}

/** La ruedita de "esperamos el resultado del proveedor", igual en las tres pantallas. */
export function WaitingIcon() {
    return (
        <StateIcon tone="info">
            <Loader2 className="size-7 animate-spin" aria-hidden />
        </StateIcon>
    );
}

/** Tarjeta de modalidad de captura (D18) — el mock la resuelve con un solo botón de inicio. */
export function ModalityButton({
    icon,
    title,
    hint,
    loading,
    onClick,
}: {
    icon: ReactNode;
    title: string;
    hint: string;
    loading?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={loading}
            className="flex flex-col items-center gap-2 rounded-lg border border-border p-5 text-center transition-colors hover:border-primary hover:bg-accent/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
            {icon}
            <span className="text-[13px] font-semibold">{title}</span>
            <span className="text-[11px] leading-relaxed text-muted-foreground">{hint}</span>
        </button>
    );
}

/**
 * Las dos formas de capturar, con el mismo par de tarjetas en las tres pantallas. Lo único que
 * cambia es el `hint`: qué le toca hacer al cliente en cada caso.
 */
export function ModalityPicker({
    tabletHint,
    phoneHint,
    loading,
    onPick,
}: {
    tabletHint: string;
    phoneHint: string;
    loading?: boolean;
    onPick: (modality: KycModality) => void;
}) {
    return (
        <div className="mx-auto mb-5 grid max-w-md grid-cols-1 gap-3 sm:grid-cols-2">
            <ModalityButton
                icon={<Smartphone className="size-6 text-primary" aria-hidden />}
                title="En esta tablet"
                hint={tabletHint}
                loading={loading}
                onClick={() => {
                    onPick('STORE_DEVICE');
                }}
            />
            <ModalityButton
                icon={<QrCode className="size-6 text-primary" aria-hidden />}
                title="En su teléfono (QR)"
                hint={phoneHint}
                loading={loading}
                onClick={() => {
                    onPick('CUSTOMER_LINK');
                }}
            />
        </div>
    );
}
